import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
// Models
import { Quotation, RequestIndicatorTypes } from 'data-layer-cr/dist/models';
// Redux
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'src/store/rootReducer';
import {
  downloadPolicyFiles,
  resetPolicyState,
} from 'src/store/policy/actions';
import {
  trackEvent,
  ITEM_CLICKED,
  FILE_MANAGER_EVENT,
  ALL_FILES_DOWNLOAD_CLICK,
} from 'src/analytics';
// Components
import { Card, Grid, Typography, Divider, makeStyles } from '@material-ui/core';
import { colors, DefaultButton } from 'design-system';
import {
  faUserLock,
  faFileShield,
  faUser,
  faCamera,
  faFileInvoice,
  faFileInvoiceDollar,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  canDownloadKYC,
  getStartDocument,
  hasIdentityDocument,
  hasOptionalIdentityDocument,
} from 'src/functions/fileManagerMethods';
import DocumentRow from './DocumentRow';
import {
  createFileList,
  getInitialFileList,
  createIndividualFileList,
} from './helperMethods';
import DownloadMessages from '../../components/DownloadMessages';

export const useStyle = makeStyles(() => ({
  cardComponent: {
    paddingTop: 30,
    paddingBottom: 15,
    paddingLeft: 25,
    paddingRight: 25,
    borderRadius: 10,
    boxShadow: '0px 5px 5px 0px rgba(0, 0, 0, 0.08)',
    marginTop: 20,
  },
  titleText: {
    fontSize: 19,
    fontWeight: 900,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gallery,
    marginTop: 20,
    marginBottom: 20,
  },
  downloadAllButton: {
    marginTop: 50,
    marginBottom: 20,
  },
}));

export const PolicyDocumentManager = ({
  quotation,
  inspectionRequired,
  downloadPolicyFilesAction,
  downloadStatus,
  resetPolicyStateAction,
}: Props): JSX.Element => {
  const classes = useStyle();
  const showIdentityDocument = hasIdentityDocument(
    quotation.additionalDocuments,
  );
  const downloadIdSecondSide = hasOptionalIdentityDocument(
    quotation.additionalDocuments,
  );
  const cancelTokenSource = useRef(axios.CancelToken.source()).current;
  const showKycDocument = canDownloadKYC(quotation.createdAt);
  const [fileList, setFileList] = useState<string[]>(getInitialFileList());
  const [openMessage, setOpenMessage] = useState<boolean>(false);
  const [isDownload, setIsDownload] = useState<boolean>(false);

  const clickCloseMessage = (): void => {
    setOpenMessage(false);
  };

  // observes when file is successfully downloaded and delivers it to the client
  useEffect(() => {
    if (downloadStatus === 'initial' && fileList.length > 0) {
      downloadPolicyFilesAction(
        quotation.urlSalt,
        fileList[0],
        cancelTokenSource,
      );
      setFileList(fileList.slice(1));
    }
    if (downloadStatus === 'requesting') {
      setIsDownload(false);
      setOpenMessage(false);
    }

    if (downloadStatus === 'success' && fileList.length === 0) {
      setIsDownload(true);
      setOpenMessage(true);
    }
    if (downloadStatus === 'failure') {
      setIsDownload(false);
      setOpenMessage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadStatus]);

  // cleanup list on unmount
  useEffect(
    () => {
      return (): void => {
        resetPolicyStateAction();
        cancelTokenSource.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDownloadAllClick = () => {
    trackEvent({
      eventName: FILE_MANAGER_EVENT,
      eventProperties: {
        [ITEM_CLICKED]: ALL_FILES_DOWNLOAD_CLICK,
      },
    });
    setFileList(
      createFileList(
        inspectionRequired,
        showIdentityDocument,
        quotation.createdAt,
        downloadIdSecondSide,
      ),
    );
    downloadPolicyFilesAction(
      quotation.urlSalt,
      getStartDocument(quotation.createdAt),
      cancelTokenSource,
    );
  };

  const handleDownloadIndividualClick = (fileName: string) => {
    setFileList(createIndividualFileList(fileName, downloadIdSecondSide));
    downloadPolicyFilesAction(quotation.urlSalt, fileName, cancelTokenSource);
  };

  return (
    <>
      {DownloadMessages({
        isOpen: openMessage,
        isSuccessMessage: isDownload,
        closeMessage: clickCloseMessage,
      })}

      <Card className={classes.cardComponent} data-testid="file-manager-card">
        <Grid container>
          <Typography className={classes.titleText}>Documentos</Typography>
        </Grid>
        <Divider className={classes.divider} />
        {showKycDocument && (
          <DocumentRow
            title="Solicitud de seguro / KYC"
            fileName="kyc"
            onClick={handleDownloadIndividualClick}
          >
            <FontAwesomeIcon icon={faUserLock} fontSize={24} color="#0D2E68" />
          </DocumentRow>
        )}
        <DocumentRow
          data-testid="policy-document-row"
          title="Póliza"
          fileName="policy"
          onClick={handleDownloadIndividualClick}
        >
          <FontAwesomeIcon icon={faFileShield} fontSize={24} color="#0D2E68" />
        </DocumentRow>
        {showIdentityDocument && (
          <DocumentRow
            data-testid="identity-file-row"
            title="Documento de identidad"
            fileName="cedula"
            onClick={handleDownloadIndividualClick}
          >
            <FontAwesomeIcon icon={faUser} fontSize={24} color="#0D2E68" />
          </DocumentRow>
        )}
        {inspectionRequired && (
          <DocumentRow
            data-testid="inspection-file-row"
            title="Inspección"
            fileName="inspectionReport"
            onClick={handleDownloadIndividualClick}
          >
            <FontAwesomeIcon icon={faCamera} fontSize={24} color="#0D2E68" />
          </DocumentRow>
        )}
        {!inspectionRequired && (
          <DocumentRow
            data-testid="invoice-file-row"
            title="Factura del vehículo"
            fileName="proforma"
            onClick={handleDownloadIndividualClick}
          >
            <FontAwesomeIcon
              icon={faFileInvoice}
              fontSize={24}
              color="#0D2E68"
            />
          </DocumentRow>
        )}
        <DocumentRow
          title="Cotización"
          fileName="quotation"
          onClick={handleDownloadIndividualClick}
        >
          <FontAwesomeIcon
            icon={faFileInvoiceDollar}
            fontSize={24}
            color="#0D2E68"
          />
        </DocumentRow>
        <Grid
          container
          justifyContent="center"
          className={classes.downloadAllButton}
        >
          <DefaultButton
            onClick={handleDownloadAllClick}
            disabled={downloadStatus === 'requesting'}
            id="sales-web-download-all-files-btn"
            data-testid="download-all-files-btn"
          >
            <FontAwesomeIcon
              icon={faArrowDownToLine}
              fontSize={24}
              color="#FFF"
            />{' '}
            &nbsp;&nbsp; descargar todo
          </DefaultButton>
        </Grid>
      </Card>
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  downloadStatus: RequestIndicatorTypes;
} => ({
  downloadStatus: state.policy.downloadStatus,
});

const reduxActions = {
  downloadPolicyFilesAction: downloadPolicyFiles,
  resetPolicyStateAction: resetPolicyState,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotation: Quotation;
  inspectionRequired: boolean;
}

export default connector(PolicyDocumentManager);
